import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { IconChevronDown, IconChevronRight, IconX } from '@tabler/icons';
import { capitalize } from 'lodash';
import { formatInTimeZone } from 'date-fns-tz';
import ReactJson from 'react-json-view';
import {
  useSurroundingLogs,
  useLoadMoreSurroundingLogs,
} from '../../../data/useSurroundingLogs';
import type { SurroundingLogEvent } from '../../../types/surroundingLogs.types';

interface SurroundingLogsDialogProps {
  open: boolean;
  onClose: () => void;
  accountId: string | undefined;
  logStream: string;
  anchorTimestamp: number;
  timezone: string;
  jobIds?: string;
}

interface ParsedEvent {
  timestamp: number;
  message: string;
  level: string;
  parsedMessage: string;
  parsedJson: Record<string, unknown>;
  error?: string;
  raw: SurroundingLogEvent;
}

function formatMessage(parsed: Record<string, any>): string {
  if (parsed['message'] === 'Request' && parsed['request']) {
    const status = parsed['response']?.status ?? '';
    const method = parsed['request']?.method ?? '';
    const url = parsed['request']?.url ?? '';
    return [status, method, url].filter(Boolean).join(' ');
  }
  return parsed['message'];
}

function parseEvent(event: SurroundingLogEvent): ParsedEvent {
  try {
    const parsed = JSON.parse(event.message);
    return {
      timestamp: event.timestamp,
      message: event.message,
      level: (parsed.level ?? 'info').toLowerCase(),
      parsedMessage: formatMessage(parsed) ?? event.message,
      parsedJson: parsed,
      error: parsed.error,
      raw: event,
    };
  } catch {
    return {
      timestamp: event.timestamp,
      message: event.message,
      level: 'info',
      parsedMessage: event.message,
      parsedJson: {},
      raw: event,
    };
  }
}

const SurroundingLogsDialog: React.FC<SurroundingLogsDialogProps> = ({
  open,
  onClose,
  accountId,
  logStream,
  anchorTimestamp,
  timezone,
  jobIds,
}) => {
  const { palette } = useTheme();
  const levelColorMap: Record<string, string> = {
    error: palette.error.dark,
    warning: palette.warning.light,
    warn: palette.warning.light,
    info: palette.info.main,
    debug: palette.grey[400],
  };

  const anchorRef = React.useRef<HTMLTableRowElement>(null);
  const [expandedRows, setExpandedRows] = React.useState<Set<number>>(
    new Set(),
  );

  const [events, setEvents] = React.useState<SurroundingLogEvent[]>([]);
  const [backwardToken, setBackwardToken] = React.useState<string | null>(null);
  const [forwardToken, setForwardToken] = React.useState<string | null>(null);
  const [hasOlderEvents, setHasOlderEvents] = React.useState(true);
  const [hasNewerEvents, setHasNewerEvents] = React.useState(true);

  const initialQuery = useSurroundingLogs(
    accountId,
    {
      log_stream: logStream,
      timestamp: anchorTimestamp,
      count: 5,
      job_ids: jobIds,
    },
    { enabled: open },
  );

  const loadMore = useLoadMoreSurroundingLogs(accountId);

  // Populate state from initial query
  React.useEffect(() => {
    if (initialQuery.data) {
      const data = initialQuery.data;
      const merged = [
        ...[...data.backward_events].reverse(),
        ...data.forward_events,
      ];
      setEvents(merged);
      setBackwardToken(data.backward_token);
      setForwardToken(data.forward_token);
      setHasOlderEvents(Boolean(data.backward_token));
      setHasNewerEvents(Boolean(data.forward_token));
    }
  }, [initialQuery.data]);

  // Auto-scroll to anchor on initial load
  React.useEffect(() => {
    if (events.length > 0 && anchorRef.current) {
      anchorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [events.length > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadOlder = () => {
    if (!backwardToken) return;
    loadMore.mutate(
      {
        log_stream: logStream,
        timestamp: anchorTimestamp,
        count: 5,
        direction: 'backward',
        next_token: backwardToken,
        job_ids: jobIds,
      },
      {
        onSuccess: (data) => {
          setEvents((prev) => [...[...data.events].reverse(), ...prev]);
          setBackwardToken(data.next_token);
          setHasOlderEvents(data.has_more);
        },
      },
    );
  };

  const handleLoadNewer = () => {
    if (!forwardToken) return;
    loadMore.mutate(
      {
        log_stream: logStream,
        timestamp: anchorTimestamp,
        count: 5,
        direction: 'forward',
        next_token: forwardToken,
        job_ids: jobIds,
      },
      {
        onSuccess: (data) => {
          setEvents((prev) => [...prev, ...data.events]);
          setForwardToken(data.next_token);
          setHasNewerEvents(data.has_more);
        },
      },
    );
  };

  const toggleRow = (idx: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const parsedEvents = React.useMemo(() => events.map(parseEvent), [events]);

  const errorMessage = React.useMemo(() => {
    if (initialQuery.error) {
      const detail = (initialQuery.error as any)?.response?.data?.detail;
      return detail || 'Failed to load surrounding logs';
    }
    return null;
  }, [initialQuery.error]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: '75vw', maxWidth: '100vw' },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6">Surrounding Logs</Typography>
        <IconButton onClick={onClose} size="small">
          <IconX size={20} />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ overflow: 'auto', flex: 1 }}>
        {initialQuery.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {errorMessage && (
          <Alert severity="error" sx={{ m: 2 }}>
            {errorMessage}
          </Alert>
        )}

        {!initialQuery.isLoading && !errorMessage && (
          <TableContainer>
            {hasOlderEvents && backwardToken && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
                <Button
                  size="small"
                  onClick={handleLoadOlder}
                  disabled={loadMore.isPending}
                >
                  {loadMore.isPending &&
                  loadMore.variables?.direction === 'backward'
                    ? 'Loading...'
                    : 'Load older events'}
                </Button>
              </Box>
            )}

            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 36, p: 0 }} />
                  <TableCell sx={{ width: 220, fontWeight: 'bold' }}>
                    Time
                  </TableCell>
                  <TableCell sx={{ width: 80, fontWeight: 'bold' }}>
                    Level
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {parsedEvents.map((event, idx) => {
                  const isAnchor = event.timestamp === anchorTimestamp;
                  const isExpanded = expandedRows.has(idx);
                  const anchorBg = isAnchor
                    ? palette.mode === 'dark'
                      ? 'rgba(25, 118, 210, 0.15)'
                      : 'rgba(25, 118, 210, 0.08)'
                    : undefined;
                  return (
                    <React.Fragment key={`${event.timestamp}-${idx}`}>
                      <TableRow
                        ref={isAnchor ? anchorRef : undefined}
                        hover
                        onClick={() => toggleRow(idx)}
                        sx={{
                          cursor: 'pointer',
                          ...(anchorBg && { backgroundColor: anchorBg }),
                        }}
                      >
                        <TableCell sx={{ p: 0, pl: 1, width: 36 }}>
                          {isExpanded ? (
                            <IconChevronDown size={16} />
                          ) : (
                            <IconChevronRight size={16} />
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            whiteSpace: 'nowrap',
                            color: palette.grey[600],
                          }}
                        >
                          {formatInTimeZone(
                            new Date(event.timestamp),
                            timezone,
                            'MMM dd, yyyy h:mm:ss.SSS a zzz',
                          )}
                        </TableCell>
                        <TableCell>
                          <span
                            style={{
                              fontWeight: 'bold',
                              fontFamily: 'monospace',
                              color: levelColorMap[event.level],
                            }}
                          >
                            {capitalize(event.level)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            title={event.parsedMessage}
                            placement="bottom-start"
                          >
                            <code
                              style={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {event.parsedMessage}
                              {event.error && (
                                <span
                                  style={{
                                    color: palette.error.dark,
                                    marginLeft: '5px',
                                  }}
                                >
                                  - {event.error}
                                </span>
                              )}
                            </code>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          sx={{
                            p: 0,
                            borderBottom: isExpanded ? undefined : 'none',
                          }}
                        >
                          <Collapse
                            in={isExpanded}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box sx={{ p: 1, pl: 5 }}>
                              <ReactJson
                                style={{ padding: '8px', cursor: 'pointer' }}
                                name={false}
                                displayObjectSize={false}
                                displayDataTypes={false}
                                collapsed={false}
                                src={event.parsedJson}
                              />
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>

            {hasNewerEvents && forwardToken && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
                <Button
                  size="small"
                  onClick={handleLoadNewer}
                  disabled={loadMore.isPending}
                >
                  {loadMore.isPending &&
                  loadMore.variables?.direction === 'forward'
                    ? 'Loading...'
                    : 'Load newer events'}
                </Button>
              </Box>
            )}
          </TableContainer>
        )}
      </Box>
    </Drawer>
  );
};

export default SurroundingLogsDialog;
